/**
 * @fileoverview Helpers for XLSX instruction worksheets (requirements and buyer companies).
 * @module utils/worksheetInstructions
 */
import { logger } from "./logger.js";
import { utils } from "xlsx-js-style";

const NOT_REQUIRED = "No requerido";
const SINGLE_COLUMN = "single-column";
const INSTRUCTION_COL_WIDTH = 20;

const applyTableBorders = (worksheet, tables) => {
  const applyBorders = (cellAddress, borderStyle) => {
    if (!worksheet[cellAddress]) {
      worksheet[cellAddress] = { v: "" };
    }
    if (!worksheet[cellAddress].s) {
      worksheet[cellAddress].s = {};
    }
    if (!worksheet[cellAddress].s.border) {
      worksheet[cellAddress].s.border = {};
    }
    worksheet[cellAddress].s.border = borderStyle;
  };

  tables.forEach((table) => {
    for (let row = table.startRow; row <= table.endRow; row++) {
      for (let col = table.startCol; col <= table.endCol; col++) {
        const cellAddress = utils.encode_cell({ r: row, c: col });
        applyBorders(cellAddress, {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        });
      }
    }
  });
};

const buildInstructionPreamble = (instructionText) => [
  [instructionText, "", ""],
  ["", "", ""],
  ["", "", ""],
];

const ensureWorksheetRows = (worksheetData, minRows) => {
  while (worksheetData.length < minRows) {
    worksheetData.push([]);
  }
};

const finalizeInstructionsWorksheet = (worksheetData, tables) => {
  const instructionsWorksheet = utils.aoa_to_sheet(worksheetData);

  const maxColumns = Math.max(...worksheetData.map((row) => row.length));
  instructionsWorksheet["!cols"] = Array(maxColumns)
    .fill()
    .map(() => ({ wch: INSTRUCTION_COL_WIDTH }));

  if (!instructionsWorksheet["!merges"]) {
    instructionsWorksheet["!merges"] = [];
  }
  instructionsWorksheet["!merges"].push({
    s: { r: 0, c: 0 },
    e: { r: 2, c: 2 },
  });

  const instructionCellAddress = utils.encode_cell({ r: 0, c: 0 });
  if (!instructionsWorksheet[instructionCellAddress]) {
    instructionsWorksheet[instructionCellAddress] = { t: "s", v: "" };
  }
  instructionsWorksheet[instructionCellAddress].s = {
    alignment: {
      wrapText: true,
      vertical: "center",
      horizontal: "center",
    },
  };

  applyTableBorders(instructionsWorksheet, tables);
  return instructionsWorksheet;
};

const getActiveSubcategories = (category) =>
  category.subcategorias ? category.subcategorias.filter((sub) => sub.activo) : [];

const buildCategorySubcategoryPairs = (activeCategories) => {
  const pairs = [];
  activeCategories.forEach((category) => {
    const activeSubcategories = getActiveSubcategories(category);
    if (activeSubcategories.length > 0) {
      activeSubcategories.forEach((subcategory) => {
        pairs.push({
          category: category.nombre,
          subcategory: subcategory.nombre,
        });
      });
    } else {
      pairs.push({ category: category.nombre, subcategory: "" });
    }
  });
  return pairs;
};

const countCategoryTableRows = (activeCategories) => {
  let totalRows = 0;
  activeCategories.forEach((category) => {
    const activeSubcategories = getActiveSubcategories(category);
    totalRows +=
      activeSubcategories.length > 0 ? activeSubcategories.length : 1;
  });
  return totalRows;
};

const getRequisitosColumn = (customerCompanyUsers) =>
  customerCompanyUsers && customerCompanyUsers.length > 0 ? 3 : 1;

const appendRequirementsUsersTable = (worksheetData, customerCompanyUsers) => {
  if (!customerCompanyUsers?.length) {
    return 0;
  }

  ensureWorksheetRows(worksheetData, 5 + customerCompanyUsers.length);
  worksheetData[4][1] = "Usuario Comprador";
  customerCompanyUsers.forEach((user, index) => {
    worksheetData[5 + index][1] = user.nombre;
  });
  return 2;
};

const appendRequirementsCategoriesTable = (
  worksheetData,
  categories,
  customerCompanyUsers,
) => {
  if (!categories?.length) {
    return;
  }

  const activeCategories = categories.filter((cat) => cat.activo);
  if (activeCategories.length === 0) {
    return;
  }

  const categorySubcategoryPairs = buildCategorySubcategoryPairs(activeCategories);
  const requisitosColumn = getRequisitosColumn(customerCompanyUsers);
  const categoriesColumn = requisitosColumn + 2;

  ensureWorksheetRows(worksheetData, 5 + categorySubcategoryPairs.length);
  worksheetData[4][categoriesColumn] = "Categoría";
  worksheetData[4][categoriesColumn + 1] = "Subcategoría";
  categorySubcategoryPairs.forEach((pair, index) => {
    worksheetData[5 + index][categoriesColumn] = pair.category;
    worksheetData[5 + index][categoriesColumn + 1] = pair.subcategory;
  });
};

const appendRequisitosValuesTable = (worksheetData, customerCompanyUsers) => {
  ensureWorksheetRows(worksheetData, 8);
  const requisitosColumn = getRequisitosColumn(customerCompanyUsers);
  worksheetData[4][requisitosColumn] = "Requisitos";
  worksheetData[5][requisitosColumn] = NOT_REQUIRED;
  worksheetData[6][requisitosColumn] = "Opcional";
  worksheetData[7][requisitosColumn] = "Requerido";
};

const buildRequirementsTableBoundaries = (customerCompanyUsers, categories) => {
  const tables = [];

  if (customerCompanyUsers?.length) {
    tables.push({
      type: SINGLE_COLUMN,
      startRow: 4,
      endRow: 4 + customerCompanyUsers.length,
      startCol: 1,
      endCol: 1,
    });
  }

  const requisitosTableColumn = getRequisitosColumn(customerCompanyUsers);
  tables.push({
    type: SINGLE_COLUMN,
    startRow: 4,
    endRow: 7,
    startCol: requisitosTableColumn,
    endCol: requisitosTableColumn,
  });

  if (categories?.length) {
    const activeCategories = categories.filter((cat) => cat.activo);
    if (activeCategories.length > 0) {
      const totalCategoryRows = countCategoryTableRows(activeCategories);
      const categoriesColumn = requisitosTableColumn + 2;
      tables.push({
        type: "two-column",
        startRow: 4,
        endRow: 4 + totalCategoryRows,
        startCol: categoriesColumn,
        endCol: categoriesColumn + 1,
      });
    }
  }

  return tables;
};

/** Creates the Requirements Instructions worksheet and appends it to the workbook. */
export const createRequirementsInstructionsWorksheet = (
  workbook,
  customerCompanyUsers,
  categories,
) => {
  logger.info("Creating Instructions worksheet with data:", {
    data: {
      customerCompanyUsers: customerCompanyUsers?.length || 0,
      categories: categories?.length || 0,
      categoriesData: categories?.map((cat) => ({
        nombre: cat.nombre,
        activo: cat.activo,
        subcategorias: cat.subcategorias?.length || 0,
        activeSubcategorias:
          cat.subcategorias?.filter((sub) => sub.activo).length || 0,
      })),
    },
  });

  const worksheetData = buildInstructionPreamble(
    "Esta es la hoja de indicaciones donde se presenta la información con la cual se puede llenar la plantilla para cargar los datos de los requerimientos",
  );

  appendRequirementsUsersTable(worksheetData, customerCompanyUsers);
  appendRequirementsCategoriesTable(
    worksheetData,
    categories,
    customerCompanyUsers,
  );
  appendRequisitosValuesTable(worksheetData, customerCompanyUsers);

  const tables = buildRequirementsTableBoundaries(
    customerCompanyUsers,
    categories,
  );
  const instructionsWorksheet = finalizeInstructionsWorksheet(
    worksheetData,
    tables,
  );
  utils.book_append_sheet(workbook, instructionsWorksheet, "Indicaciones");
};

const appendCompanySizesTable = (worksheetData, companySizes) => {
  if (!companySizes?.length) {
    return 0;
  }

  ensureWorksheetRows(worksheetData, 5 + companySizes.length);
  worksheetData[4][1] = "Tamaños de Empresa";
  companySizes.forEach((size, index) => {
    worksheetData[5 + index][1] = size.label || size.nombre || size;
  });
  return 2;
};

const appendCountryCodesTable = (worksheetData, companySizes, countryCodes) => {
  if (!countryCodes?.length) {
    return;
  }

  const companySizesTableHeight = companySizes ? companySizes.length + 1 : 0;
  const countryCodesStartRow = 5 + companySizesTableHeight + 1;

  ensureWorksheetRows(
    worksheetData,
    countryCodesStartRow + countryCodes.length,
  );

  if (companySizesTableHeight > 0) {
    worksheetData[countryCodesStartRow - 1] = [];
  }

  if (!worksheetData[countryCodesStartRow]) {
    worksheetData[countryCodesStartRow] = [];
  }
  worksheetData[countryCodesStartRow][1] = "Códigos de País";

  countryCodes.forEach((code, index) => {
    const rowIndex = countryCodesStartRow + 1 + index;
    if (!worksheetData[rowIndex]) {
      worksheetData[rowIndex] = [];
    }
    worksheetData[rowIndex][1] = code.value || code;
  });
};

const appendReferenceColumnTable = (
  worksheetData,
  items,
  header,
  startColumn,
) => {
  if (!items?.length) {
    return startColumn;
  }

  ensureWorksheetRows(worksheetData, 5 + items.length);
  worksheetData[4][startColumn + 1] = header;
  items.forEach((item, index) => {
    worksheetData[5 + index][startColumn + 1] = item.nombre;
  });
  return startColumn + 2;
};

const buildBuyerCompaniesTableBoundaries = (
  companySizes,
  states,
  towns,
  countryCodes,
) => {
  const tables = [];

  if (companySizes?.length) {
    tables.push({
      type: SINGLE_COLUMN,
      startRow: 4,
      endRow: 4 + companySizes.length,
      startCol: 1,
      endCol: 1,
    });
  }

  if (countryCodes?.length) {
    const companySizesTableHeight = companySizes ? companySizes.length + 1 : 0;
    const codesStartRow = 5 + companySizesTableHeight + 1;
    tables.push({
      type: SINGLE_COLUMN,
      startRow: codesStartRow,
      endRow: codesStartRow + countryCodes.length,
      startCol: 1,
      endCol: 1,
    });
  }

  if (states?.length) {
    const statesColumn = companySizes?.length ? 3 : 1;
    tables.push({
      type: SINGLE_COLUMN,
      startRow: 4,
      endRow: 4 + states.length,
      startCol: statesColumn,
      endCol: statesColumn,
    });
  }

  if (towns?.length) {
    let townsColumn = 1;
    if (companySizes?.length) {
      townsColumn += 2;
    }
    if (states?.length) {
      townsColumn += 2;
    }
    tables.push({
      type: SINGLE_COLUMN,
      startRow: 4,
      endRow: 4 + towns.length,
      startCol: townsColumn,
      endCol: townsColumn,
    });
  }

  return tables;
};

/** Creates the Buyer Companies Instructions worksheet and appends it to the workbook. */
export const createBuyerCompaniesInstructionsWorksheet = (
  workbook,
  companySizes = [],
  states = [],
  towns = [],
  countryCodes = [],
) => {
  logger.info("Creating Buyer Companies Instructions worksheet with data:", {
    data: {
      companySizes: companySizes?.length || 0,
      states: states?.length || 0,
      towns: towns?.length || 0,
      countryCodes: countryCodes?.length || 0,
    },
  });

  const worksheetData = buildInstructionPreamble(
    "Esta es la hoja de indicaciones donde se presenta la información con la cual se puede llenar la plantilla para cargar los datos de las empresas compradoras",
  );

  let currentColumn = appendCompanySizesTable(worksheetData, companySizes);
  appendCountryCodesTable(worksheetData, companySizes, countryCodes);
  currentColumn = appendReferenceColumnTable(
    worksheetData,
    states,
    "Estados",
    currentColumn,
  );
  appendReferenceColumnTable(worksheetData, towns, "Municipios", currentColumn);

  const tables = buildBuyerCompaniesTableBoundaries(
    companySizes,
    states,
    towns,
    countryCodes,
  );
  const instructionsWorksheet = finalizeInstructionsWorksheet(
    worksheetData,
    tables,
  );
  utils.book_append_sheet(workbook, instructionsWorksheet, "Indicaciones");
};
